import { useCallback, useRef, useState } from "react";
import { trackError } from "../telemetry/activity";

export type CameraStatus = "idle" | "requesting" | "ready" | "error";
export type CameraErrorKind =
  | "permission_denied"
  | "no_device"
  | "insecure_context"
  | "timeout"
  | "unknown";

export interface CameraController {
  status: CameraStatus;
  errorKind: CameraErrorKind | null;
  videoRef: React.RefObject<HTMLVideoElement | null>;
  start: () => Promise<void>;
  stop: () => void;
  /**
   * 현재 프레임을 캡처. `zoom`(≥1) 을 주면 프레임 중앙의 1/zoom 영역만 잘라 담는다 —
   * CameraProvider 가 프리뷰를 같은 배율로 확대해 보여주므로 화면과 결과가 일치한다 (BOOTH-23).
   */
  capture: (zoom?: number) => string | null;
}

/**
 * 센서 네이티브 4:3 의 최대 해상도를 요청한다 (`ideal` 이라 기기가 주는 가장 가까운 포맷으로 내려간다).
 * 이전엔 1920×1080(16:9) 을 요청해 iPad 세로에서 3:4 크롭 시 위아래 25% 를 버렸고, 변환 입력 사진이
 * 약 800×1067(0.85MP) 에 그쳤다. 4:3 스트림은 세로 3:4 와 같은 비율이라 크롭 손실이 없고,
 * 1440×1920 이상을 주는 기기에선 입력 픽셀이 1.5–7배 늘어난다. 폭 기준 FOV 는 16:9 와 같다(iOS 는
 * 16:9 를 4:3 센서의 상하 크롭으로 만든다) — 프리뷰(cover, 폭 맞춤) 얼굴 크기는 변하지 않는다.
 * 16:9 만 지원하는 웹캠은 그 안에서 가장 큰 포맷을 고르므로 기존과 같다.
 */
const CONSTRAINTS: MediaStreamConstraints = {
  video: {
    facingMode: "user",
    width: { ideal: 4032 },
    height: { ideal: 3024 },
  },
  audio: false,
};

/**
 * 캡처 JPEG 품질. 변환 전 크롭(`cropDataUrlToAspect`) 에서 한 번 더 인코딩될 수 있으므로 1세대 손실을 최소화한다.
 * 0.92 → 0.95 는 파일 크기 약 1.4배, 블록 노이즈는 눈에 띄게 줄어든다.
 */
const CAPTURE_JPEG_QUALITY = 0.95;

/** getUserMedia 가 응답하지 않을 때(일부 브라우저·드라이버) "연결 중"에 영원히 머물지 않게 하는 상한 */
const GET_USER_MEDIA_TIMEOUT_MS = 12_000;

/**
 * 카메라 장치가 하나라도 있는지 빠르게 확인. 권한 전이라 label 은 비어 있어도 kind 는 알 수 있다.
 * enumerateDevices 를 못 쓰는 환경이면 null(모름) → getUserMedia 에 맡긴다.
 */
async function hasVideoInput(): Promise<boolean | null> {
  try {
    if (!navigator.mediaDevices?.enumerateDevices) return null;
    const devices = await navigator.mediaDevices.enumerateDevices();
    if (devices.length === 0) return null; // 일부 브라우저는 권한 전엔 빈 배열
    return devices.some((d) => d.kind === "videoinput");
  } catch {
    return null;
  }
}

function withTimeout<T>(p: Promise<T>, ms: number): Promise<T> {
  return new Promise((resolve, reject) => {
    const t = setTimeout(() => reject(new DOMException("camera timeout", "TimeoutError")), ms);
    p.then(
      (v) => { clearTimeout(t); resolve(v); },
      (e) => { clearTimeout(t); reject(e); }
    );
  });
}

function classifyError(err: unknown): CameraErrorKind {
  if (typeof window !== "undefined" && !window.isSecureContext) {
    return "insecure_context";
  }
  if (err instanceof DOMException) {
    if (err.name === "NotAllowedError" || err.name === "SecurityError") {
      return "permission_denied";
    }
    if (err.name === "NotFoundError" || err.name === "OverconstrainedError") {
      return "no_device";
    }
    if (err.name === "TimeoutError") {
      return "timeout";
    }
  }
  return "unknown";
}

export function useCamera(): CameraController {
  const [status, setStatus] = useState<CameraStatus>("idle");
  const [errorKind, setErrorKind] = useState<CameraErrorKind | null>(null);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const stoppedIntentionallyRef = useRef(false);
  const isRequestingRef = useRef(false);

  const attachStreamToVideo = useCallback((stream: MediaStream) => {
    const video = videoRef.current;
    if (!video) return;
    video.srcObject = stream;
    void video.play().catch(() => {
      // autoplay 정책 충돌 시 무시 — muted + playsInline 로 해결되어야 함
    });
  }, []);

  const start = useCallback(async () => {
    if (streamRef.current) {
      setStatus("ready");
      setErrorKind(null);
      attachStreamToVideo(streamRef.current);
      return;
    }
    if (isRequestingRef.current) return;
    if (typeof window !== "undefined" && !window.isSecureContext) {
      setStatus("error");
      setErrorKind("insecure_context");
      return;
    }
    isRequestingRef.current = true;
    stoppedIntentionallyRef.current = false;
    setStatus("requesting");
    setErrorKind(null);
    try {
      if (!navigator.mediaDevices?.getUserMedia) {
        throw new DOMException("mediaDevices unavailable", "NotFoundError");
      }
      // 장치가 아예 없으면 getUserMedia 를 기다리지 않고 바로 "카메라 없음"
      if ((await hasVideoInput()) === false) {
        throw new DOMException("no video input", "NotFoundError");
      }
      const stream = await withTimeout(
        navigator.mediaDevices.getUserMedia(CONSTRAINTS),
        GET_USER_MEDIA_TIMEOUT_MS
      );
      // 타임아웃 뒤 늦게 도착한 스트림은 stop() 이 이미 불렸을 수 있음 — 의도적 정지면 즉시 해제
      if (stoppedIntentionallyRef.current) {
        stream.getTracks().forEach((t) => t.stop());
        return;
      }
      streamRef.current = stream;
      attachStreamToVideo(stream);

      // 권한 철회·디바이스 분리 등 외부 요인으로 트랙 종료 시 에러로 전환
      // 우리가 명시적으로 stop() 한 경우는 stoppedIntentionallyRef 로 제외
      stream.getVideoTracks().forEach((track) => {
        track.addEventListener("ended", () => {
          if (stoppedIntentionallyRef.current) return;
          streamRef.current = null;
          setStatus("error");
          setErrorKind("unknown");
        });
      });

      setStatus("ready");
    } catch (err) {
      streamRef.current = null;
      setStatus("error");
      const kind = classifyError(err);
      setErrorKind(kind);
      if (kind === "permission_denied") {
        trackError("camera_denied", err);
      } else {
        trackError("camera_unavailable", err, { message: kind });
      }
    } finally {
      isRequestingRef.current = false;
    }
  }, [attachStreamToVideo]);

  const stop = useCallback(() => {
    stoppedIntentionallyRef.current = true;
    const stream = streamRef.current;
    if (stream) {
      stream.getTracks().forEach((t) => t.stop());
    }
    streamRef.current = null;
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
    setStatus("idle");
    setErrorKind(null);
  }, []);

  const capture = useCallback((zoom = 1): string | null => {
    const video = videoRef.current;
    const stream = streamRef.current;
    if (!video || !stream) return null;
    const track = stream.getVideoTracks()[0];
    if (!track) return null;
    const settings = track.getSettings();
    const frameW = settings.width ?? video.videoWidth;
    const frameH = settings.height ?? video.videoHeight;
    if (!frameW || !frameH) return null;

    // 확대 배율만큼 프레임 중앙을 잘라 담는다 (zoom 1 = 전체 프레임)
    const z = Number.isFinite(zoom) && zoom > 1 ? zoom : 1;
    const width = Math.round(frameW / z);
    const height = Math.round(frameH / z);
    const sx = Math.round((frameW - width) / 2);
    const sy = Math.round((frameH - height) / 2);

    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext("2d");
    if (!ctx) return null;

    // 미러링: 프리뷰와 동일하게 좌우 반전 저장
    ctx.translate(width, 0);
    ctx.scale(-1, 1);
    ctx.drawImage(video, sx, sy, width, height, 0, 0, width, height);

    return canvas.toDataURL("image/jpeg", CAPTURE_JPEG_QUALITY);
  }, []);

  return { status, errorKind, videoRef, start, stop, capture };
}
